import { orderBy, query, Unsubscribe, where } from 'firebase/firestore';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useAuthContext } from '../contexts/auth.context';
import { Habit } from '../dto/habit';
import { Record, RecordCollection, RecordConverter } from '../dto/record';
import { User, UserCollection } from '../dto/user';
import { useDataStore } from '../stores/data.store';
import { getDay, getIsoDay } from '../util/util';
import { useFirebase } from './firebase.hook';

export interface RecordMap {
  [habitId: string]: Record[] | undefined;
}

export interface RecordInterface {
  loadRecords(user: User): Unsubscribe;
  resetRecords(): void;
  recordsLoading: boolean;
  records: Record[];
  recordsByHabit: RecordMap;

  recordsFor: (habit: Habit, from?: Date, to?: Date) => Record[];
  addOrUpdateRecord: (habit: Habit, date: Date, achievement: number) => Promise<Record | undefined>;
  deleteRecord: (record: Record) => Promise<void>;
  deleteRecords: (user: User, habit?: Habit) => Promise<void>;

  recordsForPartner: (user: User) => Promise<RecordMap>;
}

export function useRecord(): RecordInterface {
  const { user } = useAuthContext();
  const { setDocument, deleteDocument, collection, queryRun, querySubscribe, deleteQuery, waitIfOnline } =
    useFirebase();

  const [storeRecords, setStoreRecords] = useDataStore(useShallow((s) => [s.records, s.setRecords]));
  const recordsByHabit = useMemo(() => toMap(storeRecords), [storeRecords]);

  // DB methods
  function loadRecords(user: User): Unsubscribe {
    return querySubscribe<Record>(
      query(
        collection(UserCollection, user.id, RecordCollection).withConverter(RecordConverter),
        orderBy('date', 'asc'),
      ),
      setStoreRecords,
    );
  }

  function resetRecords() {
    setStoreRecords(undefined);
  }

  // user methods
  function recordsFor(habit: Habit, from?: Date, to?: Date): Record[] {
    return recordsByHabit[habit.id]?.filter((r) => (!from || r.date >= from) && (!to || r.date < to)) ?? [];
  }

  async function addOrUpdateRecord(habit: Habit, date: Date, achievement: number): Promise<Record | undefined> {
    if (!user) return;

    const record: Record = {
      habitId: habit.id,
      id: `${getIsoDay(date)}_${habit.id}`,
      date: getDay(date),
      createdAt: new Date(),
      target: habit.target.value,
      achievement: achievement,
    };

    return waitIfOnline(setDocument(`${UserCollection}/${user.id}/${RecordCollection}`, record, record.id));
  }

  async function deleteRecord(record: Record): Promise<void> {
    if (!user) return;

    await waitIfOnline(deleteDocument(`${UserCollection}/${user.id}/${RecordCollection}`, record.id));
  }

  async function deleteRecords(user: User, habit?: Habit): Promise<void> {
    const coll = collection(UserCollection, user.id, RecordCollection);
    return deleteQuery(habit ? query(coll, where('habitId', '==', habit.id)) : coll);
  }

  async function recordsForPartner(partner: User): Promise<RecordMap> {
    const records = await queryRun<Record>(
      query(
        collection(UserCollection, partner.id, RecordCollection).withConverter(RecordConverter),
        orderBy('date', 'asc'),
      ),
    );
    return toMap(records);
  }

  // helper methods
  function toMap(records?: Record[]): RecordMap {
    return (records ?? []).reduce((prev, curr) => {
      prev[curr.habitId] = (prev[curr.habitId] ?? []).concat(curr);
      return prev;
    }, {} as RecordMap);
  }

  return useMemo(
    () => ({
      loadRecords,
      resetRecords,
      recordsLoading: user != null && !storeRecords,
      records: storeRecords ?? [],
      recordsByHabit,
      recordsFor,
      addOrUpdateRecord,
      deleteRecord,
      deleteRecords,
      recordsForPartner,
    }),
    [storeRecords, user, setDocument, deleteDocument, collection, queryRun, querySubscribe, deleteQuery, waitIfOnline],
  );
}
